import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { t } from '@lingui/macro'
import { isNil } from 'ramda'
import { Link } from 'react-router-dom'
import { useTheme } from 'styled-components'

import { Chip } from '@/@bothrs-ui/web/components/chip/Chip'
import { Flex, FlexColumn, FlexRow, Margin } from '@/@bothrs-ui/web/components/layout'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { WebPlatform } from '@/capacitor/components/native-platform/NativePlatform'
import { mixpanel } from '@/mixpanel'
import { RouteCard } from '@/modules/app/components/route-card/RouteCard'
import { formatToWaitTime, formatUpcomingDaysDifference } from '@/utils/time'
import { StCard, StImageContainer } from '../UpcomingAppointmentSection.styled'

type Props = {
  id: number
  type: 'Appointment' | 'Encounter'
  descriptions?: string[]
  daysToAppointment?: number
  waitTime?: number
  careplan?: string
  address?: string
  onClick?: () => void
}

const Card = ({ id, type, descriptions, daysToAppointment, waitTime, careplan, address, onClick }: Props) => {
  const theme = useTheme()
  const canShowRoute = address && !isNil(daysToAppointment) && daysToAppointment <= 2

  const link =
    type === 'Appointment' ? `/timeline/appointment/${id}` : type === 'Encounter' ? `/timeline/encounter/${id}` : ''

  // Interaction Methods
  // ------------------------------------------------------------------------- /
  function trackEvent() {
    mixpanel.track(`home_feed_${type.toLowerCase()}_detail`)
  }

  return (
    <>
      <Link
        to={link}
        onClick={() => {
          onClick?.()
          trackEvent()
        }}
      >
        <StCard>
          <WebPlatform>
            <StImageContainer>
              <img src='/img/svg/empty-calendar.svg' alt='' />
            </StImageContainer>
          </WebPlatform>

          <FlexColumn flexGrow={1} gap={theme.UI.Spacing.Space1} alignSelf='stretch'>
            <FlexRow alignItems='center' justifyContent='space-between'>
              <H3 noMargin color={theme.Components.Card.Text}>
                {type === 'Appointment'
                  ? t({ id: 'globals.appointment' })
                  : type === 'Encounter'
                    ? t({ id: 'globals.intake' })
                    : null}
              </H3>
              {isNil(daysToAppointment) ? null : (
                <Chip type='Secondary'>
                  <Span type='BodySmallRegular'>{formatUpcomingDaysDifference(daysToAppointment)}</Span>
                </Chip>
              )}
            </FlexRow>

            {descriptions?.map((value, index) => (
              <Flex key={`${value}_${index}`} justifyContent='space-between' alignItems='center'>
                <Span key={`${value}_${index}`} type='BodyMediumRegular' color={theme.Components.Card.Text}>
                  {`${value}
                ${
                  index === 0 && waitTime ? (
                    <Span type='BodyMediumRegular' color={theme.Colors.Status.Error}>
                      {formatToWaitTime(waitTime)}
                    </Span>
                  ) : (
                    ''
                  )
                }`}
                </Span>

                {index === descriptions.length - 1 ? (
                  <FontAwesomeIcon icon={['far', 'chevron-right']} fontSize={16} />
                ) : null}
              </Flex>
            ))}
          </FlexColumn>

          {careplan ? (
            <>
              <Margin mb={theme.UI.Spacing.Space3} />
              <Chip type='Primary'>{careplan}</Chip>
            </>
          ) : null}
        </StCard>
      </Link>
      {canShowRoute ? (
        <>
          <Margin mb={theme.UI.Spacing.Space2} />
          <RouteCard key='route-card-content' address={address} />
        </>
      ) : null}
    </>
  )
}

export type { Props as CardProps }
export { Card }
